import { Field, useField } from 'formik';
import {
  CircularProgress,
  TextField as TextFieldMaterial
} from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteRenderInputParams
} from 'formik-material-ui-lab';
import React, { FC, useState } from 'react';
import { useBusinessSectors } from 'app/client/clientBusiness/hooks/useBusinessSectors';
import { Button } from '@vadiun/react-components';
import SectorCreateEditModal from './SectorCreateEditModal';
import {
  BusinessSector,
  SectorPayloadToCreate
} from 'app/client/clientBusiness/models/businessSectors';
import { AutocompleteRenderOptionState } from '@material-ui/lab/Autocomplete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useVerifyAction } from '@vadiun/react-hooks-legacy';
interface SectorFieldProps {
  name: string;
  classname: string;
  withCreate?: boolean;
}
const SectorField: FC<SectorFieldProps> = ({
  name,
  classname,
  withCreate = true
}) => {
  const verifyAction = useVerifyAction();
  const { sectors, loading, addSector, editSector, deleteSector } =
    useBusinessSectors();
  const [field, meta, helpers] = useField(name);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSector, setEditingSector] = useState<BusinessSector | null>(
    null
  );

  const create = async (payload: SectorPayloadToCreate) => {
    await addSector.mutate(payload);
  };

  const edit = async (id: number, payload: SectorPayloadToCreate) => {
    await editSector.mutate(id, payload);
    if (field.value?.id === editingSector?.id) {
      helpers.setValue({ id, name: payload.name });
    }
  };

  const handleCancel = () => {
    setEditingSector(null);
    setShowCreateModal(false);
  };

  const handleEditClick = (e: React.MouseEvent, rubro: BusinessSector) => {
    e.stopPropagation();
    setEditingSector(rubro);
    setShowCreateModal(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, rubro: BusinessSector) => {
    e.stopPropagation();
    verifyAction({
      title: `¡Estás por eliminar un rubro: ${rubro.name}!`,
      body: 'Esta acción va a eliminar permanentemente el rubro. ¿Estás seguro?',
      onAccept: async () => {
        await deleteSector.mutate(rubro.id);
        if (field.value?.id === rubro.id) {
          helpers.setValue(null);
        }
      }
    });
  };

  return (
    <>
      <div className="flex w-full gap-1">
        <Field
          className={classname}
          name={name}
          component={Autocomplete}
          options={sectors}
          getOptionLabel={(option: BusinessSector) => option.name}
          renderOption={(
            option: BusinessSector,
            state: AutocompleteRenderOptionState
          ) => (
            <div className="flex w-full items-center justify-between">
              <span>{option.name}</span>
              <div className="flex gap-1">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    onClick={(e) => handleEditClick(e, option)}
                    size="1x"
                    color="dodgerblue"
                    icon={faPencilAlt}
                    className=" hover:text-blue-600"
                    aria-label="edit"
                  />
                </div>
                <div className="flex items-center">
                  <FontAwesomeIcon
                    onClick={(e) => handleDeleteClick(e, option)}
                    icon={faTrash}
                    color="red"
                    className="hover:text-red-600"
                    aria-label="delete"
                  />
                </div>
              </div>
            </div>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextFieldMaterial
              {...params}
              error={meta.touched && !!meta.error}
              helperText={meta.error}
              label="Rubro"
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
        {withCreate && (
          <div className="flex justify-end items-center">
            <Button
              type="button"
              variant="contained"
              onClick={() => setShowCreateModal(true)}
              className="flex justify-center items-center h-8 w-8 pt-3"
            >
              <p className="text-3xl leading-none">+</p>
            </Button>
          </div>
        )}
      </div>
      {showCreateModal && (
        <SectorCreateEditModal
          isOpen={showCreateModal}
          onSubmit={async (payload: SectorPayloadToCreate) => {
            if (editingSector) {
              await edit(editingSector.id, payload);
            } else {
              await create(payload);
            }
            handleCancel();
          }}
          onCancel={handleCancel}
          initialValues={{ name: editingSector?.name || '' }}
        />
      )}
    </>
  );
};

export default SectorField;
